// ==================================
// Componente Autocomplete
// ==================================

import { useState, useRef, useEffect, type ReactNode } from 'react';

interface AutocompleteOption {
  value: string;
  label: string;
}

interface AutocompleteProps {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: ReactNode;
  options: AutocompleteOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function Autocomplete({
  label,
  error,
  helperText,
  icon,
  options,
  value,
  onChange,
  placeholder,
  disabled,
}: AutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync display text with selected value
  const selectedLabel = options.find((o) => o.value === value)?.label || value;

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (opt: AutocompleteOption) => {
    onChange(opt.value);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleInputChange = (text: string) => {
    setQuery(text);
    if (!open) setOpen(true);
  };

  const handleFocus = () => {
    if (!disabled) {
      setOpen(true);
      setQuery('');
    }
  };

  const displayValue = open ? query : selectedLabel;

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-stone-700 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-stone-400 z-10">
            {icon}
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full py-2.5 pr-10 text-sm text-obsidian-900 placeholder-stone-400
            bg-white border rounded-xl
            transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-offset-0
            disabled:bg-stone-50 disabled:text-stone-500 disabled:cursor-not-allowed
            ${icon ? 'pl-10' : 'pl-4'}
            ${
              error
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                : 'border-stone-300 hover:border-stone-400 focus:border-primary-500 focus:ring-primary-500/20'
            }
          `}
        />
        {/* Chevron icon */}
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg
            className={`w-4 h-4 text-stone-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Dropdown */}
        {open && filtered.length > 0 && (
          <div className="absolute z-50 w-full mt-1.5 bg-white border border-stone-200 rounded-xl shadow-lg shadow-stone-200/50 max-h-52 overflow-auto animate-in fade-in slide-in-from-top-1 duration-150">
            {filtered.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelect(opt)}
                  className={`
                    w-full text-left px-4 py-2.5 text-sm transition-colors
                    first:rounded-t-xl last:rounded-b-xl
                    ${
                      isSelected
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-stone-700 hover:bg-stone-50'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <span>{opt.label}</span>
                    {isSelected && (
                      <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* No results */}
        {open && query && filtered.length === 0 && (
          <div className="absolute z-50 w-full mt-1.5 bg-white border border-stone-200 rounded-xl shadow-lg shadow-stone-200/50 p-4 text-center">
            <p className="text-sm text-stone-400">No se encontraron colonias</p>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className="mt-1.5 text-sm text-stone-500">{helperText}</p>
      )}
    </div>
  );
}

export default Autocomplete;
